/**
 * #342 — the camera pin's six shapes as numbers, moved from components/map/CameraPinSvg.tsx (which draws them and
 * sizes the alert from them) so a lib can read where a pin's head is without importing a component: the wedge's
 * direction tick (lib/wedgeStyle tickLengthPx) runs from the pin's tip to past its head. Units are the shape's viewBox
 * units; the pin stands on the bottom centre of its viewBox, its tip on the camera's point.
 */
import type { PinShape } from '@/types'

export interface ShapeSpec {
  w: number             // viewBox width
  h: number             // viewBox height
  // where the number (or the centre dot) sits, and how far the head body reaches from it (its farthest point,
  // without the outline); the alert centres here and takes every size from it (#306 ripple)
  head: { cx: number; cy: number; r: number }
  numberY: [number, number]   // baseline for a short number, for four digits
  numberSize: [number, number]
  dot: { cx: number; cy: number }    // the red offline dot
}
export const SHAPES: Record<PinShape, ShapeSpec> = {
  round: { w: 24, h: 30, head: { cx: 12, cy: 12, r: 10.5 }, numberY: [15.6, 14.7], numberSize: [9.5, 7.6], dot: { cx: 19.5, cy: 5 } },
  // the teardrop (and the camera below) fills the 24-unit viewBox, so half its outline is clipped and the visible
  // edge is the path's r 12 itself; the half-outline pinHeadRadius adds is then ~1 px of air: harmless
  teardrop: { w: 24, h: 36, head: { cx: 12, cy: 12, r: 12 }, numberY: [15.4, 14.7], numberSize: [9, 7.2], dot: { cx: 19, cy: 5 } },
  dot: { w: 24, h: 24, head: { cx: 12, cy: 12, r: 10.6 }, numberY: [15.4, 14.6], numberSize: [9.5, 7.6], dot: { cx: 19.5, cy: 4.5 } },
  // the label's rounded corners are its farthest points: 13.65 to a corner's arc centre, plus the 2.5 corner radius
  tag: { w: 32, h: 25.2, head: { cx: 16, cy: 10.5, r: 16.15 }, numberY: [14, 13.6], numberSize: [9.5, 8.5], dot: { cx: 28.5, cy: 3.5 } },
  lollipop: { w: 24, h: 36, head: { cx: 12, cy: 10, r: 9.3 }, numberY: [13.3, 12.8], numberSize: [9, 7.2], dot: { cx: 18.8, cy: 3.4 } },
  camera: { w: 24, h: 36, head: { cx: 12, cy: 12, r: 12 }, numberY: [15.4, 14.7], numberSize: [9, 7.2], dot: { cx: 19, cy: 5 } },
}

/** The pin image grows this many times, from its tip, while active (hovered, its card open, dragged or selected):
 *  index.css `.detercam-pin--active > svg`; mapPinStyles.test.ts holds the two together. */
export const ACTIVE_PIN_SCALE = 1.1

/** #306 — an alerting pin is drawn this many times larger, from its tip (index.css `.detercam-pin--alert > svg` and
 *  the bounce keyframes use the same number), also while active: that rule comes later. #342 — moved here from
 *  CameraPinSvg.tsx (which re-exports it) so the wedge tick can measure an alerting pin. */
export const ALERT_PIN_SCALE = 1.25
